<app-pagetoolbar label="Participants" icon="manage_accounts"></app-pagetoolbar>
<mat-accordion multi="false">
  <div class="filter-section">
    <mat-form-field>
      <mat-label>Filter participants</mat-label>
      <input matInput [formControl]="searchControl" placeholder="Type to filter..." />
      <button *ngIf="searchControl.value" matSuffix mat-icon-button aria-label="Clear" (click)="searchControl.setValue('')">
        <mat-icon>close</mat-icon>
      </button>
    </mat-form-field>

    <mat-button-toggle-group [hideSingleSelectionIndicator]="true" appearance="legacy">
      <mat-button-toggle
        value="nonpm"
        [checked]="_filterButtons.value === 'nonpm'"
        (click)="setFilterMode('nonpm', $event)">
        {{ pmLabel$ | async }}
        <mat-icon *ngIf="_filterButtons.value === 'nonpm'">exposure</mat-icon>
      </mat-button-toggle>
      <mat-button-toggle 
        #conf 
        value="warning" 
        [checked]="_filterButtons.value === 'warning'"
        (click)="setFilterMode('warning', $event)">
        {{ warningLabel$ | async }}
        <mat-icon *ngIf="_filterButtons.value === 'warning'">exposure</mat-icon>
      </mat-button-toggle>
      <mat-button-toggle 
        #conf2 
        value="priority" 
        [checked]="_filterButtons.value === 'priority'"
        (click)="setFilterMode('priority', $event)">
        {{ priorityLabel$ | async }}
        <mat-icon *ngIf="_filterButtons.value === 'priority'">exposure</mat-icon>
      </mat-button-toggle>
      <mat-button-toggle 
        #conf3 
        value="pair" 
        [checked]="_filterButtons.value === 'pair'"
        (click)="setFilterMode('pair', $event)">
        {{ pairLabel$ | async }}
        <mat-icon *ngIf="_filterButtons.value === 'pair'">exposure</mat-icon>
      </mat-button-toggle>
      <mat-button-toggle 
        #conf5 
        value="active" 
        [checked]="_filterButtons.value === 'active'"
        (click)="setFilterMode('active', $event)">
        {{ activeLabel$ | async }}
        <mat-icon *ngIf="_filterButtons.value === 'active'">exposure</mat-icon>
      </mat-button-toggle>
    </mat-button-toggle-group>
  </div>
  <mat-expansion-panel
    *ngFor="let participant of filteredParticipants; let i = index"
    class="pair"
    [class.inactive]="shouldGrayOutParticipant(participant)"
    id="participant-{{ participant.id }}"
    (opened)="select(participant)"
    (opened)="panelOpenState[i] = true"
    (closed)="panelOpenState[i] = false"
  >
    <mat-expansion-panel-header>
      <!-- Header for paired recipients: show ENIS+name, "Recipient", center, then donor info -->
      <ng-container *ngIf="isRecipient(participant) && participant.linkedDonors && participant.linkedDonors.length > 0">
        <mat-panel-description class="small-description">
          <span>Recipient</span>
        </mat-panel-description>
        <mat-panel-title>
          <b class="random-number"><span [innerHTML]="participant.enisNumber | highlight: searchControl.value || ''"></span></b>
          <span class="ellipsis-container participant-name">
            <span
              *ngIf="participant.firstName || participant.lastName; else showRecipientDob"
              [innerHTML]="(participant.firstName || '') + ' ' + (participant.lastName || '') | highlight: searchControl.value || ''"
            ></span>
            <ng-template #showRecipientDob> <span [innerHTML]="participant.dob | highlight: searchControl.value || ''"></span> </ng-template>
          </span>
        </mat-panel-title>
        <mat-panel-description
          class="center-name"
          [innerHTML]="participant.center.name | highlight:(searchControl.value || '')">
        </mat-panel-description>
        <mat-panel-description class="ki-urgency">
          {{ participant.urgency || '' }}
        </mat-panel-description>
        <mat-panel-description class="participant-type-label">
          <span *ngIf="participant.linkedDonors.length === 1" style="display: flex; align-items: center; gap: 8px;">
            <span style="font-weight: 500;">Donor</span>
            <b class="random-number">{{ participant.linkedDonors[0].enisNumber }}</b>
            <span style="font-weight: normal;">{{ participant.linkedDonors[0].firstName }} {{ participant.linkedDonors[0].lastName }}</span>
          </span>
          <span *ngIf="participant.linkedDonors.length > 1">
            Multiple Donors
          </span>
        </mat-panel-description>
        <mat-panel-description class="warning-chips-container">
          <app-warning-chips
            [recipient]="participant"
            [donor]="getCurrentDonor(participant)"
            [pair]="getPairByParticipantId(participant?.id || '')"
            [pairs]="getAllPairsForParticipant(participant?.id || '')"
            [isPanelOpen]="panelOpenState[i]"
            [canDismissMultipleDonorWarning]="canDismissMultipleDonorWarning"
            [canDismissAcceptableBloodTypesWarning]="canDismissAcceptableBloodTypesWarning"
            [canDismissMaxDonorAgeWarning]="canDismissMaxDonorAgeWarning"
            [canDismissMaxMismatchesWarning]="canDismissMaxMismatchesWarning"
            (pairUpdated)="onPairUpdated($event)"
            (multipleDonorWarningClicked)="onMultipleDonorWarningClick($event)"
            (acceptableBloodTypesWarningClicked)="onAcceptableBloodTypesWarningClick($event)"
            (maxDonorAgeWarningClicked)="onMaxDonorAgeWarningClick($event)"
            (maxMismatchesWarningClicked)="onMaxMismatchesWarningClick($event)"
          >
          </app-warning-chips>
        </mat-panel-description>
      </ng-container>

      <!-- Header for solo participants (recipients without donors, or solo donors) -->
      <ng-container *ngIf="!(isRecipient(participant) && participant.linkedDonors && participant.linkedDonors.length > 0)">
        <mat-panel-description class="participant-type-label">
          {{ isDonor(participant) ? (participant.altruist ? "Altruist" : "Donor") : "Recipient" }}
        </mat-panel-description>
        <mat-panel-title style="margin-left: 19.8px;">
          <b class="random-number"><span [innerHTML]="participant.enisNumber | highlight: searchControl.value || ''"></span></b>
          <span class="ellipsis-container participant-name">
            <span
              *ngIf="participant.firstName || participant.lastName; else showDob"
              [innerHTML]="(participant.firstName || '') + ' ' + (participant.lastName || '') | highlight: searchControl.value || ''"
            ></span>
            <ng-template #showDob> <span [innerHTML]="participant.dob | highlight: searchControl.value || ''"></span> </ng-template
          ></span>
        </mat-panel-title>
        <mat-panel-description
          class="center-name"
          [innerHTML]="participant.center.name | highlight:(searchControl.value || '')">
        </mat-panel-description>
        <mat-panel-description class="ki-urgency">
          {{ isRecipient(participant) ? (participant.urgency || '') : '' }}
        </mat-panel-description>
        <mat-panel-description class="warning-chips-container">
          <app-warning-chips
            [recipient]="isRecipient(participant) ? participant : undefined"
            [donor]="isDonor(participant) ? participant : undefined"
            [pair]="getPairByParticipantId(participant?.id || '')"
            [pairs]="getAllPairsForParticipant(participant?.id || '')"
            [isPanelOpen]="panelOpenState[i]"
            [canDismissMultipleDonorWarning]="canDismissMultipleDonorWarning"
            [canDismissAcceptableBloodTypesWarning]="canDismissAcceptableBloodTypesWarning"
            [canDismissMaxDonorAgeWarning]="canDismissMaxDonorAgeWarning"
            [canDismissMaxMismatchesWarning]="canDismissMaxMismatchesWarning"
            (pairUpdated)="onPairUpdated($event)"
            (multipleDonorWarningClicked)="onMultipleDonorWarningClick($event)"
            (acceptableBloodTypesWarningClicked)="onAcceptableBloodTypesWarningClick($event)"
            (maxDonorAgeWarningClicked)="onMaxDonorAgeWarningClick($event)"
            (maxMismatchesWarningClicked)="onMaxMismatchesWarningClick($event)"
          >
          </app-warning-chips>
        </mat-panel-description>
      </ng-container>
    </mat-expansion-panel-header>

    <!-- Single pair inactive banner (only for participants with 1 pair that is inactive) -->
    <div class="inactive-reason" *ngIf="getSingleInactivePair(participant) as inactivePair">Inactive with reason: {{ inactivePair.reasonInactive }}</div>

    <!-- Inactive altruist banner -->
    <div class="inactive-reason" *ngIf="getInactiveAltruist(participant) as inactiveAltruist">
      Inactive altruistic donor{{ inactiveAltruist.reasonInactive ? ' with reason: ' + inactiveAltruist.reasonInactive : '' }}
    </div>

    <!-- Inactive donor banner (non-altruist, no pairs) -->
    <div class="inactive-reason" *ngIf="getInactiveDonor(participant) as inactiveDonor">
      Inactive donor{{ inactiveDonor.reasonInactive ? ' with reason: ' + inactiveDonor.reasonInactive : '' }}
    </div>

    <!-- Inactive recipient banner (no pairs) -->
    <div class="inactive-reason" *ngIf="getInactiveRecipient(participant) as inactiveRecipient">
      Inactive recipient{{ inactiveRecipient.reasonInactive ? ' with reason: ' + inactiveRecipient.reasonInactive : '' }}
    </div>

    <!-- Inactive group banner (with multiple pairs) -->
    <div class="inactive-reason" *ngIf="getInactiveRecipientWithMultiplePairs(participant) as inactiveRecipient">
      Inactive group{{ inactiveRecipient.reasonInactive ? ' with reason: ' + inactiveRecipient.reasonInactive : '' }}
    </div>

    <!-- Participant boxes layout -->
    <div
      [ngClass]="{
        'participant-boxes separator-bottom responsive-layout': isRecipient(participant) && participant?.linkedDonors?.length
      }"
    >
      <!-- For paired recipients: show recipient first, then donor(s) with navigation -->
      <ng-container *ngIf="isRecipient(participant) && participant.linkedDonors && participant.linkedDonors.length > 0">
        <!-- Recipient box (left side) -->
        <app-participant-box
          [recipient]="participant"
          [pair]="getPairByParticipantId(participant?.id || '')"
          [hideBanners]="true"
          (pairUpdated)="onPairUpdated($event)"
          (pairDeleted)="onPairDeleted($event)"
          (recipientUpdated)="onRecipientUpdated($event)"
          [isMultiDonor]="participant.linkedDonors.length > 1"
          [canEditParticipant]="canEditParticipant"
          [canSetTransplanted]="canSetTransplanted"
          [canRemoveParticipant]="canRemoveParticipant"
          [canAddPriorityParticipant]="canAddPriorityParticipant"
          [canAddPrivilegesParticipant]="canAddPrivilegesParticipant"
          [canCenterUserEditParticipant]="canCenterUserEditParticipant(participant)"
          [canCenterUserArchiveParticipant]="canCenterUserArchiveParticipant(participant)"
          (editParticipantClicked)="editParticipant($event)"
          (removeParticipantClicked)="removeParticipant($event)"
          (requestPriorityClicked)="requestPriority($event)"
          (requestPrivilegeClicked)="requestPrivilege($event)"
        >
        </app-participant-box>

        <!-- Donor box(es) with navigation (right side) -->
        <div style="position: relative">
          <!-- Navigation buttons for multiple donors -->
          <div *ngIf="participant.linkedDonors.length > 1" style="position: absolute; top: 50px; right: 60px; z-index: 10; display: flex; gap: 5px; align-items: center; justify-content: flex-end; min-width: 120px">
            <button
              mat-icon-button
              (click)="previousDonor(participant.id || '', participant.linkedDonors.length)"
              matTooltip="Previous donor"
              [style.visibility]="getCurrentDonorIndex(participant.id || '') > 0 ? 'visible' : 'hidden'"
            >
              <mat-icon>chevron_left</mat-icon>
            </button>
            <span style="font-size: 14px; min-width: 40px; text-align: center">{{ getCurrentDonorIndex(participant.id || '') + 1 }} / {{ participant.linkedDonors.length }}</span>
            <button
              mat-icon-button
              (click)="nextDonor(participant.id || '', participant.linkedDonors.length)"
              matTooltip="Next donor"
              [style.visibility]="getCurrentDonorIndex(participant.id || '') < participant.linkedDonors.length - 1 ? 'visible' : 'hidden'"
            >
              <mat-icon>chevron_right</mat-icon>
            </button>
          </div>

          <!-- Display current donor -->
          <app-participant-box
            *ngIf="getCurrentDonor(participant) as currentDonor"
            [donor]="currentDonor"
            [pair]="getPairByDonorId(currentDonor?.id || '')"
            [hideBanners]="true"
            (pairDeleted)="onPairDeleted($event)"
            (pairUpdated)="onPairUpdated($event)"
            (donorUpdated)="onDonorUpdated($event)"
            [isMultiDonor]="participant.linkedDonors.length > 1"
            [recipientFromGroup]="participant"
            [canEditParticipant]="canEditParticipant"
            [canSetTransplanted]="canSetTransplanted"
            [canRemoveParticipant]="canRemoveParticipant"
            [canAddPrivilegesParticipant]="canAddPrivilegesParticipant"
            [canCenterUserEditParticipant]="canCenterUserEditParticipant(currentDonor)"
            [canCenterUserArchiveParticipant]="canCenterUserArchiveParticipant(currentDonor)"
            (editParticipantClicked)="editParticipant($event)"
            (removeParticipantClicked)="removeParticipant($event)"
            (requestPrivilegeDonorClicked)="requestPrivilegeDonor($event)"
          ></app-participant-box>
        </div>

        <!-- Shared banners container for pair view -->
        <div class="pair-banners" *ngIf="getCurrentDonor(participant) as currentDonor">
          <!-- Recipient banner -->
          <div *ngIf="isParticipantLockedHelper(participant)">
            <div class="locked-message">
              <mat-icon>lock</mat-icon>
              <span>{{ getParticipantLockMessage(participant) }}</span>
            </div>
          </div>
          <!-- Donor banner -->
          <div *ngIf="isParticipantLockedHelper(currentDonor)">
            <div class="locked-message">
              <mat-icon>lock</mat-icon>
              <span>{{ getParticipantLockMessage(currentDonor) }}</span>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- For solo participants (recipients without donors, or solo donors): show single box -->
      <ng-container *ngIf="!(isRecipient(participant) && participant.linkedDonors && participant.linkedDonors.length > 0)">
        <app-participant-box
          [pair]="getPairByParticipantId(participant?.id || '')"
          (pairUpdated)="onPairUpdated($event)"
          (pairDeleted)="onPairDeleted($event)"
          (donorUpdated)="onDonorUpdated($event)"
          (recipientUpdated)="onRecipientUpdated($event)"
          [recipient]="isRecipient(participant) ? participant : undefined"
          [donor]="isDonor(participant) ? participant : undefined"
          [isMultiDonor]="false"
          [canEditParticipant]="canEditParticipant"
          [canSetTransplanted]="canSetTransplanted"
          [canRemoveParticipant]="canRemoveParticipant"
          [canAddPriorityParticipant]="canAddPriorityParticipant"
          [canAddPrivilegesParticipant]="canAddPrivilegesParticipant"
          [canCenterUserEditParticipant]="canCenterUserEditParticipant(participant)"
          [canCenterUserArchiveParticipant]="canCenterUserArchiveParticipant(participant)"
          (editParticipantClicked)="editParticipant($event)"
          (removeParticipantClicked)="removeParticipant($event)"
          (requestPriorityClicked)="requestPriority($event)"
          (requestPrivilegeClicked)="requestPrivilege($event)"
          (requestPrivilegeDonorClicked)="requestPrivilegeDonor($event)"
        >
        </app-participant-box>
      </ng-container>
    </div>
    <!-- Pair-related action buttons -->
    <mat-action-row class="pair-action-row">
      <!-- For paired recipients: show pair management buttons -->
      <ng-container *ngIf="isRecipient(participant) && participant.linkedDonors && participant.linkedDonors.length > 0">
        <!-- Individual pair/donor management (works for both single pairs and multi-donor groups) -->
        <ng-container *ngIf="getCurrentDonor(participant) as currentDonor">
          <ng-container *ngIf="getPairByDonorId(currentDonor.id || '') as currentPair">
            <div class="pair-action-left">
              <a href="/history/pair/{{ currentPair.id }}" class="pair-history-link">See pair history</a>
            </div>
            <div class="pair-action-right">
              <!-- Bulk group management (only for multi-donor scenarios) -->
              <!-- When group is ACTIVE: show group deactivate button -->
              <button *ngIf="participant.linkedDonors.length > 1 && participant.active !== false && hasActivePair(participant) && canInactivatePair && !isParticipantLocked(participant)"
                      mat-button [matMenuTriggerFor]="recipientBulkDeactivateMenu" class="custom-button">
                Set group inactive
              </button>
              <mat-menu #recipientBulkDeactivateMenu="matMenu">
                <button *ngFor="let reason of getRecipientWithdrawReasons() | keyvalue" (click)="deactivateRecipientWithPairs(participant, reason)" mat-menu-item>
                  {{ reason.value }}
                </button>
              </mat-menu>

              <!-- When group is INACTIVE: show group activate and change reason buttons -->
              <button *ngIf="participant.linkedDonors.length > 1 && participant.active === false && !hasActivePair(participant) && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)"
                      mat-button class="custom-button" (click)="activateRecipientWithPairs(participant)">
                Set group active
              </button>

              <button *ngIf="participant.linkedDonors.length > 1 && participant.active === false && !hasActivePair(participant) && canInactivatePair && !isParticipantLocked(participant)"
                      mat-button [matMenuTriggerFor]="changeGroupReasonMenu" class="custom-button">
                Change inactive reason group
              </button>
              <mat-menu #changeGroupReasonMenu="matMenu">
                <button *ngFor="let reason of getRecipientWithdrawReasons() | keyvalue" (click)="changeRecipientInactiveReasonFromParticipant(participant, reason)" mat-menu-item>
                  {{ reason.value }}
                </button>
              </mat-menu>

              <!-- For multi-donor groups: manage individual donors (ONLY when group is ACTIVE) -->
              <button *ngIf="participant.linkedDonors.length > 1 && participant.active !== false && currentPair.active && canInactivatePair && !isParticipantLocked(participant)" mat-button [matMenuTriggerFor]="withdrawMenu" class="custom-button">Set current donor inactive</button>
              <button *ngIf="participant.linkedDonors.length > 1 && participant.active !== false && !currentPair.active && canInactivatePair && !isParticipantLocked(participant) && canActivatePairEntity(currentPair)" mat-button class="custom-button" (click)="activatePairFromParticipant(currentPair)">Set current donor active</button>

              <button *ngIf="participant.linkedDonors.length > 1 && participant.active !== false && !currentPair.active && canInactivatePair && !isParticipantLocked(participant) && canActivatePairEntity(currentPair)" mat-button [matMenuTriggerFor]="changeDonorReasonMenu" class="custom-button">Change inactive reason donor</button>
              <mat-menu #changeDonorReasonMenu="matMenu">
                <button *ngFor="let reason of getDonorWithdrawReasons() | keyvalue" (click)="changeInactiveReasonFromParticipant(currentPair, reason)" mat-menu-item>
                  {{ reason.value }}
                </button>
              </mat-menu>

              <!-- For single pairs: manage pairs -->
              <button *ngIf="participant.linkedDonors.length === 1 && currentPair.active && canInactivatePair && !isParticipantLocked(participant)" mat-button [matMenuTriggerFor]="pairWithdrawMenu" class="custom-button">Set pair inactive</button>
              <button *ngIf="participant.linkedDonors.length === 1 && !currentPair.active && canInactivatePair && !isParticipantLocked(participant) && canActivatePairEntity(currentPair)" mat-button class="custom-button" (click)="activatePairFromParticipant(currentPair)">Set pair active</button>

              <button *ngIf="participant.linkedDonors.length === 1 && !currentPair.active && canInactivatePair && !isParticipantLocked(participant) && canActivatePairEntity(currentPair)" mat-button [matMenuTriggerFor]="changeReasonMenu" class="custom-button">Change inactive reason</button>
              <mat-menu #changeReasonMenu="matMenu">
                <button *ngFor="let reason of getWithdrawReasons() | keyvalue" (click)="changeInactiveReasonFromParticipant(currentPair, reason)" mat-menu-item>
                  {{ reason.value }}
                </button>
              </mat-menu>

              <mat-menu #pairWithdrawMenu="matMenu">
                <button *ngFor="let reason of getWithdrawReasons() | keyvalue" (click)="withdrawPairFromParticipant(currentPair, reason)" mat-menu-item>
                  {{ reason.value }}
                </button>
              </mat-menu>

              <mat-menu #withdrawMenu="matMenu">
                <button *ngFor="let reason of getDonorWithdrawReasons() | keyvalue" (click)="withdrawPairFromParticipant(currentPair, reason)" mat-menu-item>
                  {{ reason.value }}
                </button>
              </mat-menu>

              <button *ngIf="canEditPair && !isParticipantLocked(participant)" mat-button class="warn-button" (click)="removePairFromParticipant(currentPair)">Archive pair</button>
              <button *ngIf="canEditPair && !isParticipantLocked(participant)" mat-button class="warn-button" (click)="editPairFromParticipant(currentPair)">Unpair</button>
            </div>
          </ng-container>
        </ng-container>
        <button *ngIf="canAddPair && canAddDonor(participant) && !isParticipantLocked(participant)" mat-button class="custom-button" (click)="addDonor(participant)">Add donor</button>
      </ng-container>

      <!-- For solo altruists: show altruist activation buttons -->
      <ng-container *ngIf="isDonor(participant) && participant.altruist && !participant.linkedRecipient">
        <button *ngIf="participant.active && canInactivatePair && !isParticipantLocked(participant)" mat-button [matMenuTriggerFor]="altruistDeactivateMenu" class="custom-button">Set altruist inactive</button>
        <mat-menu #altruistDeactivateMenu="matMenu">
          <button *ngFor="let reason of getDonorWithdrawReasons() | keyvalue" (click)="deactivateAltruistFromParticipant(participant, reason)" mat-menu-item>
            {{ reason.value }}
          </button>
        </mat-menu>
        <button *ngIf="!participant.active && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)" mat-button [matMenuTriggerFor]="altruistChangeReasonMenu" class="custom-button">Change inactive reason</button>
        <mat-menu #altruistChangeReasonMenu="matMenu">
          <button *ngFor="let reason of getDonorWithdrawReasons() | keyvalue" (click)="changeAltruistInactiveReasonFromParticipant(participant, reason)" mat-menu-item>
            {{ reason.value }}
          </button>
        </mat-menu>
        <button *ngIf="!participant.active && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)" mat-button class="custom-button" (click)="activateAltruistFromParticipant(participant)">Set altruist active</button>
      </ng-container>

      <!-- For solo donors (non-altruist): show donor activation buttons -->
      <ng-container *ngIf="isDonor(participant) && !participant.altruist && !participant.linkedRecipient">
        <button *ngIf="participant.active !== false && canInactivatePair && !isParticipantLocked(participant)" mat-button [matMenuTriggerFor]="donorDeactivateMenu" class="custom-button">Set donor inactive</button>
        <mat-menu #donorDeactivateMenu="matMenu">
          <button *ngFor="let reason of getDonorWithdrawReasons() | keyvalue" (click)="deactivateDonorFromParticipant(participant, reason)" mat-menu-item>
            {{ reason.value }}
          </button>
        </mat-menu>
        <button *ngIf="participant.active === false && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)" mat-button [matMenuTriggerFor]="donorChangeReasonMenu" class="custom-button">Change inactive reason</button>
        <mat-menu #donorChangeReasonMenu="matMenu">
          <button *ngFor="let reason of getDonorWithdrawReasons() | keyvalue" (click)="changeDonorInactiveReasonFromParticipant(participant, reason)" mat-menu-item>
            {{ reason.value }}
          </button>
        </mat-menu>
        <button *ngIf="participant.active === false && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)" mat-button class="custom-button" (click)="activateDonorFromParticipant(participant)">Set donor active</button>
        <button *ngIf="canAddPair && canAddRecipient(participant) && !isParticipantLocked(participant)" mat-button class="custom-button" (click)="addRecipient(participant)">Add recipient</button>
      </ng-container>

      <!-- For solo recipients: show recipient activation buttons -->
      <ng-container *ngIf="isRecipient(participant) && (!participant.linkedDonors || participant.linkedDonors.length === 0)">
        <button *ngIf="participant.active !== false && canInactivatePair && !isParticipantLocked(participant)" mat-button [matMenuTriggerFor]="recipientDeactivateMenu" class="custom-button">Set recipient inactive</button>
        <mat-menu #recipientDeactivateMenu="matMenu">
          <button *ngFor="let reason of getRecipientWithdrawReasons() | keyvalue" (click)="deactivateRecipientFromParticipant(participant, reason)" mat-menu-item>
            {{ reason.value }}
          </button>
        </mat-menu>
        <button *ngIf="participant.active === false && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)" mat-button [matMenuTriggerFor]="recipientChangeReasonMenu" class="custom-button">Change inactive reason</button>
        <mat-menu #recipientChangeReasonMenu="matMenu">
          <button *ngFor="let reason of getRecipientWithdrawReasons() | keyvalue" (click)="changeRecipientInactiveReasonFromParticipant(participant, reason)" mat-menu-item>
            {{ reason.value }}
          </button>
        </mat-menu>
        <button *ngIf="participant.active === false && canInactivatePair && !isParticipantLocked(participant) && canActivateParticipant(participant)" mat-button class="custom-button" (click)="activateRecipientFromParticipant(participant)">Set recipient active</button>
        <button *ngIf="canAddPair && canAddDonor(participant) && !isParticipantLocked(participant)" mat-button class="custom-button" (click)="addDonor(participant)">Add donor</button>
      </ng-container>
    </mat-action-row>
  </mat-expansion-panel>
</mat-accordion>

<div class="participant-count">
  {{ filteredParticipantsCount }} of {{ totalParticipantsCount }} participants shown
</div>

<button *ngIf="canImportParticipants" id="enis-import" (click)="importEnisData()" mat-fab matTooltip="Import ENIS Data">
  <mat-icon>import_export</mat-icon>
</button>

<button *ngIf="canAddParticipant" id="new-participant" (click)="addParticipant()" mat-fab matTooltip="Add Participant">
  <mat-icon>person_add</mat-icon>
</button>

<span *ngIf="canToggleMinimatchStatus">
  <button id="toggle-minimatch" (click)="toggleMinimatchStatus()" mat-fab
    [disabled]="isTogglingMinimatch"
    matTooltip="Mini-match Settings">
    <mat-icon>settings</mat-icon>
  </button>
</span>
